import { useState, useEffect } from 'react';
import {
  SitecoreContextResourceType,
  TenantApplicationNode,
  DeploymentInfo,
  DeploymentResourceContext,
} from '../types/types';
import { useDeploymentInfoCache } from './useDeploymentInfoCache';
import { logError } from '../utils/errorUtils';

const getDeploymentInfoFromResources = ({
  resources,
  applicationsNodes,
}: DeploymentResourceContext): {
  projectId: string;
  environmentId: string;
} | null => {
  if (!resources || resources.length === 0) {
    return null;
  }
  if (!applicationsNodes || applicationsNodes.length === 0) {
    return null;
  }

  try {
    const findAnnotationsInAllNodes = () => {
      for (const node of applicationsNodes) {
        const projectId = node.annotations.find(
          (a) =>
            a.key === 'XMCloud.ProjectId' ||
            a.key === 'XmCloud.ProjectId' ||
            (a.key.toLowerCase().includes('project') &&
              a.key.toLowerCase().includes('id')),
        )?.value;

        const environmentId = node.annotations.find(
          (a) =>
            a.key === 'XMCloud.EnvironmentId' ||
            a.key === 'XmCloud.EnvironmentId' ||
            (a.key.toLowerCase().includes('environment') &&
              a.key.toLowerCase().includes('id')),
        )?.value;

        if (projectId && environmentId) {
          return { projectId, environmentId };
        }
      }

      return null;
    };

    const extractAnnotationValues = (
      tenantNode: TenantApplicationNode,
    ): { projectId: string; environmentId: string } | null => {
      const projectIdKeys = ['XMCloud.ProjectId', 'projectId'];
      const environmentIdKeys = ['XMCloud.EnvironmentId', 'environmentId'];

      let projectId = '';
      for (const key of projectIdKeys) {
        const value = tenantNode.annotations.find((a) => a.key === key)?.value;
        if (value) {
          projectId = value;
          break;
        }
      }

      let environmentId = '';
      for (const key of environmentIdKeys) {
        const value = tenantNode.annotations.find((a) => a.key === key)?.value;
        if (value) {
          environmentId = value;
          break;
        }
      }

      if (!projectId) {
        const projectAnnotation = tenantNode.annotations.find(
          (a) =>
            a.key.toLowerCase().includes('project') &&
            a.key.toLowerCase().includes('id'),
        );
        if (projectAnnotation) {
          projectId = projectAnnotation.value;
        }
      }

      if (!environmentId) {
        const envAnnotation = tenantNode.annotations.find(
          (a) =>
            a.key.toLowerCase().includes('environment') &&
            a.key.toLowerCase().includes('id'),
        );
        if (envAnnotation) {
          environmentId = envAnnotation.value;
        }
      }

      if (!projectId || !environmentId) {
        return null;
      }
      return { projectId, environmentId };
    };

    const relevantResources = resources.filter(
      (r) =>
        r.tenantProductCode === 'Edge' || r.tenantProductCode === 'XMCloud',
    );

    if (relevantResources.length === 0) {
      return null;
    }

    const sortedResources = [...relevantResources].sort((a, b) => {
      const aHasSlash = a.tenantName?.includes('/') ? 1 : 0;
      const bHasSlash = b.tenantName?.includes('/') ? 1 : 0;
      return bHasSlash - aHasSlash;
    });

    for (const resource of sortedResources) {
      if (!resource.tenantId) {
        continue;
      }

      const tenantNode = applicationsNodes.find(
        (node) => node.id === resource.tenantId,
      );
      if (!tenantNode) {
        continue;
      }

      const extractedInfo = extractAnnotationValues(tenantNode);
      if (extractedInfo) {
        return extractedInfo;
      }
    }

    const fallbackInfo = findAnnotationsInAllNodes();
    if (fallbackInfo) {
      return fallbackInfo;
    }
    return null;
  } catch (error) {
    return null;
  }
};

export const useContextDeploymentInfo = (
  sitecoreContextId: string,
  resources: SitecoreContextResourceType[] | undefined,
  applicationsNodes: TenantApplicationNode[],
): DeploymentInfo => {
  const [deploymentInfo, setDeploymentInfo] = useState<DeploymentInfo>({
    projectId: '',
    environmentId: '',
    isLoading: true,
    error: null,
  });

  const { getFromCache, addToCache } = useDeploymentInfoCache();

  useEffect(() => {
    const fetchDeploymentInfo = async () => {
      try {
        const cachedInfo = getFromCache(sitecoreContextId);

        if (cachedInfo && cachedInfo.projectId && cachedInfo.environmentId) {
          setDeploymentInfo({
            ...cachedInfo,
            isLoading: false,
            error: null,
          });
          return;
        }

        const info = getDeploymentInfoFromResources({
          resources,
          applicationsNodes,
        });
        if (info) {
          const newDeploymentInfo = {
            projectId: info.projectId,
            environmentId: info.environmentId,
            isLoading: false,
            error: null,
          };

          setDeploymentInfo(newDeploymentInfo);

          addToCache(sitecoreContextId, newDeploymentInfo);
        } else {
          setDeploymentInfo({
            projectId: '',
            environmentId: '',
            isLoading: false,
            error: new Error('Failed to retrieve deployment information'),
          });
        }
      } catch (error) {
        const errorInfo = {
          projectId: '',
          environmentId: '',
          isLoading: false,
          error: error instanceof Error ? error : new Error('Unknown error'),
        };

        setDeploymentInfo(errorInfo);
        logError('useContextDeploymentInfo', error, { sitecoreContextId });
      }
    };

    fetchDeploymentInfo();
  }, [
    sitecoreContextId,
    resources,
    applicationsNodes,
    getFromCache,
    addToCache,
  ]);

  return deploymentInfo;
};
